import {
  configureStore,
  createAction,
  createAsyncThunk,
  type ThunkDispatch,
  type ThunkMiddleware,
  type Tuple,
} from "@reduxjs/toolkit";
import { createContext, useState, type JSX, type PropsWithChildren } from "react";
import {
  createDispatchHook,
  createSelectorHook,
  Provider,
  type ReactReduxContextValue,
} from "react-redux";
import type { AccessGrant } from "../iam/schemas.ts";
import type { ORPCClient } from "../orpc-client.ts";
import { useORPCClient } from "../orpc-react.tsx";
import type { Logger } from "../services/logger.ts";

type ThunkContext = { logger: Logger; orpc: ORPCClient };

type OTPState = { status: "idle" } | { status: "using passcode"; passcode: string };

const otpReset = createAction("reset");

const otpUsePasscode = createAction("use passcode", (newPasscode: string) => ({
  payload: { newPasscode },
}));

type OTPSupportedAction = ReturnType<typeof otpReset> | ReturnType<typeof otpUsePasscode>;

function otpDefaultState(): OTPState {
  return { status: "idle" };
}

function otpReducer(currentState: OTPState | undefined, action: OTPSupportedAction): OTPState {
  switch (action.type) {
    case "reset":
      if (currentState?.status === "idle") return currentState;
      return otpDefaultState();

    case "use passcode":
      if (
        currentState?.status === "using passcode" &&
        currentState.passcode === action.payload.newPasscode
      )
        return currentState;
      return {
        passcode: action.payload.newPasscode,
        status: "using passcode",
      };

    default:
      return currentState ?? otpDefaultState();
  }
}

const OTPContext = createContext<ReactReduxContextValue<OTPState, OTPSupportedAction> | null>(null);
const useOTPDispatch = createDispatchHook(OTPContext);
const useOTPSelector = createSelectorHook(OTPContext);

const OTP = {
  Context: OTPContext,
  reducer: otpReducer,
  useDispatch: useOTPDispatch,
  useSelector: useOTPSelector,
};

type AccessorState =
  | { status: "idle" }
  | { status: "authenticating"; usingPasscode: string }
  | {
      status: "authenticated";
      accessGrant: AccessGrant;
      usingPasscode?: string;
    }
  | { status: "error"; error: string };

const accessorRequestAccess = createAction(
  "request access",
  (payload: { usingPasscode: string }) => ({ payload }),
);

const accessorUseAccessGrant = createAction(
  "use access grant",
  (payload: { accessGrant: AccessGrant }) => ({ payload }),
);

const accessorEncounterError = createAction("encounter error", (payload: { error: string }) => ({
  payload,
}));

const accessorReset = createAction("reset");

type AccessorSupportedAction =
  | ReturnType<typeof accessorRequestAccess>
  | ReturnType<typeof accessorUseAccessGrant>
  | ReturnType<typeof accessorEncounterError>
  | ReturnType<typeof accessorReset>;

const accessorThunk = createAsyncThunk.withTypes<{
  extra: ThunkContext;
  state: AccessorState;
  dispatch: ThunkDispatch<AccessorState, ThunkContext, AccessorSupportedAction>;
}>();

const accessorSilentlyRequestAccess = accessorThunk(
  "silently request access",
  (payload: { usingPasscode: string }, { dispatch, extra: { logger, orpc }, getState }) => {
    const currentState = getState();
    if (
      currentState.status === "authenticated" ||
      (currentState.status === "authenticating" &&
        currentState.usingPasscode === payload.usingPasscode)
    )
      return;
    orpc.iam.requestAccess({ signInToken: payload.usingPasscode }).then(
      (accessGrant) => dispatch(accessorUseAccessGrant({ accessGrant })),
      (error) =>
        logger.warn(
          `Error silently obtaining access grant with passcode ${payload.usingPasscode}: `,
          error,
        ),
    );
    logger.debug(`Requested access grant using passcode ${payload.usingPasscode}...`);
  },
);

const accessorExplicitlyRequestAccess = accessorThunk(
  "explicitly request access",
  (payload: { usingPasscode: string }, { dispatch, extra: { logger: _logger, orpc } }) => {
    orpc.iam.requestAccess({ signInToken: payload.usingPasscode }).then(
      (accessGrant) => dispatch(accessorUseAccessGrant({ accessGrant })),
      (error) => dispatch(accessorEncounterError({ error })),
    );
    dispatch(accessorRequestAccess({ usingPasscode: payload.usingPasscode }));
  },
);

const accessorAutoRequestAccess = accessorThunk(
  "auto request access",
  (payload: { usingPasscode: string }, { dispatch, getState }) => {
    const currentState = getState();
    switch (currentState.status) {
      case "authenticated":
      case "authenticating":
        if (currentState.usingPasscode === payload.usingPasscode) return;
        return dispatch(accessorSilentlyRequestAccess(payload));

      case "error":
      case "idle":
        return dispatch(accessorExplicitlyRequestAccess(payload));
    }
  },
);

function accessorDefaultState(): AccessorState {
  return { status: "idle" };
}

function accessorReducer(
  currentState: AccessorState | undefined,
  action: AccessorSupportedAction,
): AccessorState {
  switch (action.type) {
    case "encounter error":
      if (currentState?.status === "error" && currentState.error === action.payload.error)
        return currentState;
      return { status: "error", error: action.payload.error };

    case "use access grant":
      if (
        currentState?.status === "authenticated" &&
        currentState.accessGrant.accessToken === action.payload.accessGrant.accessToken
      )
        return currentState;
      return {
        status: "authenticated",
        accessGrant: action.payload.accessGrant,
        usingPasscode:
          currentState?.status === "authenticating" ? currentState.usingPasscode : undefined,
      };

    case "request access":
      if (
        currentState?.status === "authenticating" &&
        currentState.usingPasscode === action.payload.usingPasscode
      )
        return currentState;
      return {
        status: "authenticating",
        usingPasscode: action.payload.usingPasscode,
      };

    case "reset":
      if (currentState?.status === "idle") return currentState;
      return accessorDefaultState();

    default:
      return currentState ?? accessorDefaultState();
  }
}

const AccessorContext = createContext<ReactReduxContextValue<
  AccessorState,
  AccessorSupportedAction
> | null>(null);
const useAccessorDispatch = createDispatchHook(AccessorContext);
const useAccessorSelector = createSelectorHook(AccessorContext);

const Accessor = {
  Context: AccessorContext,
  reducer: accessorReducer,
  useDispatch: useAccessorDispatch,
  useSelector: useAccessorSelector,
  autoRequestAccess: accessorAutoRequestAccess,
};

export function auth(ctx: ThunkContext) {
  const otp = configureStore({
    middleware: (
      getDefaultMiddleware,
    ): Tuple<[ThunkMiddleware<OTPState, OTPSupportedAction, ThunkContext>]> =>
      getDefaultMiddleware({
        thunk: { extraArgument: ctx },
      }),
    devTools: { name: "OTP" },
    reducer: OTP.reducer,
  });

  const accessor = configureStore({
    devTools: { name: "Accessor" },
    middleware: (
      getDefaultMiddleware,
    ): Tuple<[ThunkMiddleware<AccessorState, AccessorSupportedAction, ThunkContext>]> =>
      getDefaultMiddleware({
        thunk: { extraArgument: ctx },
      }),
    reducer: Accessor.reducer,
  });

  otp.subscribe(() => {
    const otpState = otp.getState();

    switch (otpState.status) {
      case "using passcode":
        accessor.dispatch(Accessor.autoRequestAccess({ usingPasscode: otpState.passcode }));
    }
  });

  return { accessor, otp };
}

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const orpc = useORPCClient();
  const [state] = useState(() => auth({ logger: console, orpc }));

  return (
    <Provider context={OTP.Context} store={state.otp}>
      <Provider context={Accessor.Context} store={state.accessor}>
        {children}
      </Provider>
    </Provider>
  );
}
